import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Logo } from "../../components/Logo";
import { ChangePasswordModal } from "../../components/ChangePasswordModal";
import { Bus as BusIcon, LogOut, ArrowRight, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { refreshCache, syncQueue } from "../../lib/offlineStore";
import { storeSetJSON } from "../../lib/nativeStore";

export default function OperatorBuses() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [buses, setBuses] = useState([]);
    const [changePasswordOpen, setChangePasswordOpen] = useState(false);

    useEffect(() => {
        api.get("/buses").then((r) => setBuses(r.data));
        // Bus selection, before heading out, is the operator's best shot at having signal for
        // the rest of the shift — refresh the offline eligibility cache and flush any scans
        // still queued from the last dead zone while we can. Both best-effort/silent.
        refreshCache(api);
        syncQueue(api);
    }, []);

    const handleLogout = async () => {
        await logout();
        toast.success("Signed out");
        navigate("/login", { replace: true });
    };

    const pick = async (bus) => {
        // Await the save — the scan screen reads it on mount, so navigating before it lands
        // would race and find no bus.
        await storeSetJSON("scanix_selected_bus", bus);
        navigate("/operator/scan");
    };

    // Highlight buses assigned to the current operator
    const assigned = buses.filter((b) => b.operator_id === user?.id);
    const others = buses.filter((b) => b.operator_id !== user?.id);

    return (
        <div className="min-h-screen bg-white">
            <header className="border-b border-[#E5E5E5] px-5 py-4 flex items-center justify-between">
                <Logo size="sm" />
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => setChangePasswordOpen(true)}
                        data-testid="operator-change-password"
                        className="flex items-center gap-2 text-sm font-bold text-[#111111]"
                    >
                        <KeyRound size={16} /> Password
                    </button>
                    <button
                        onClick={handleLogout}
                        data-testid="operator-logout"
                        className="flex items-center gap-2 text-sm font-bold text-[#111111]"
                    >
                        <LogOut size={16} /> Sign out
                    </button>
                </div>
            </header>
            {changePasswordOpen && <ChangePasswordModal onClose={() => setChangePasswordOpen(false)} />}

            <main className="p-5 sm:p-8 max-w-2xl mx-auto">
                <p className="label-caps mb-2">Operator</p>
                <h1 className="font-display text-4xl sm:text-5xl mb-1">Good to see you,</h1>
                <h2 className="font-display text-4xl sm:text-5xl text-[#FF3B30] mb-6">
                    {user?.name?.split(" ")[0]}.
                </h2>
                <p className="text-[#666666] mb-10">
                    Select the bus you are boarding students onto. You can scan QR codes in the next step.
                </p>

                {assigned.length > 0 && (
                    <>
                        <p className="label-caps mb-3">Assigned To You</p>
                        <div className="space-y-3 mb-8" data-testid="assigned-buses">
                            {assigned.map((b) => (
                                <BusCard key={b.id} bus={b} onPick={pick} highlight />
                            ))}
                        </div>
                    </>
                )}

                {others.length > 0 && (
                    <>
                        <p className="label-caps mb-3">All Buses</p>
                        <div className="space-y-3" data-testid="other-buses">
                            {others.map((b) => (
                                <BusCard key={b.id} bus={b} onPick={pick} />
                            ))}
                        </div>
                    </>
                )}
            </main>
        </div>
    );
}

function BusCard({ bus, onPick, highlight }) {
    return (
        <button
            onClick={() => onPick(bus)}
            data-testid={`select-bus-${bus.bus_name.replace(/\s/g, "-")}`}
            className={`w-full text-left border transition-all p-5 flex items-center gap-4 hover:-translate-y-0.5 hover:shadow-lg ${
                highlight
                    ? "bg-[#111111] text-white border-[#111111]"
                    : "bg-white text-[#111111] border-[#E5E5E5] hover:border-[#111111]"
            }`}
        >
            <div
                className={`w-12 h-12 flex items-center justify-center flex-shrink-0 ${
                    highlight ? "bg-[#FF3B30] text-white" : "bg-[#F8F9FA] text-[#111111]"
                }`}
            >
                <BusIcon size={22} />
            </div>
            <div className="flex-1">
                <div className="font-display text-2xl">{bus.bus_name}</div>
                <div className={`text-sm ${highlight ? "text-white/70" : "text-[#666666]"}`}>
                    {bus.route_name}
                </div>
            </div>
            <ArrowRight size={20} />
        </button>
    );
}
